/**
 * Brand logo for an email sender, used in inbox/search avatars.
 *
 * When the sender's domain looks like a real organisation (not a consumer
 * mailbox like gmail/gmx) we ask Google's favicon index for a 128px icon.
 * That service 404s when it has nothing useful, which is how we fall back to
 * coloured initials — the same pattern Gmail uses for unknown people.
 */

export const EMAIL_AVATAR_COLORS = [
  '#4285F4', '#EA4335', '#34A853', '#FBBC04', '#FF6D01',
  '#46BDC6', '#7B1FA2', '#C2185B', '#0097A7', '#689F38',
  '#F4511E', '#5C6BC0', '#00897B', '#D81B60',
];

/** Icons smaller than this are Google's 16px globe / a pixelated ico — not a logo. */
export const MIN_SENDER_LOGO_PX = 48;

const CONSUMER_MAILBOX_DOMAINS = new Set([
  'gmail.com', 'googlemail.com',
  'outlook.com', 'outlook.de', 'hotmail.com', 'hotmail.de',
  'live.com', 'live.de', 'msn.com',
  'yahoo.com', 'yahoo.de', 'ymail.com',
  'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'aol.de',
  'gmx.de', 'gmx.net', 'gmx.at', 'gmx.ch', 'gmx.com',
  'web.de', 't-online.de',
  'mail.com', 'email.com',
  'protonmail.com', 'proton.me', 'pm.me',
  'posteo.de', 'mailbox.org',
  'freenet.de', 'arcor.de',
  '1und1.de', 'ionos.de',
  'zoho.com', 'zohomail.com',
  'yandex.com', 'yandex.ru',
  'mail.ru',
  'fastmail.com', 'hey.com',
  'tutanota.com', 'tuta.com',
]);

const MAIL_HOST_PREFIXES = new Set([
  'mail', 'email', 'e', 'news', 'newsletter', 'noreply', 'no-reply',
  'smtp', 'mx', 'mailer', 'bounce', 'info', 'smtp-out', 'outbound',
]);

const logoStatusByDomain = new Map();

export function getEmailInitials(name) {
  if (!name) return '?';
  const parts = String(name).split(/[\s,]+/).filter(Boolean);
  if (parts.length >= 2) return (parts[0][0] + parts[1][0]).toUpperCase();
  const clean = String(name).replace(/[^a-zA-Z0-9]/g, '');
  return (clean.substring(0, 2) || '?').toUpperCase();
}

export function getEmailAvatarColor(name) {
  if (!name) return EMAIL_AVATAR_COLORS[0];
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = ((hash << 5) - hash + name.charCodeAt(i)) | 0;
  return EMAIL_AVATAR_COLORS[Math.abs(hash) % EMAIL_AVATAR_COLORS.length];
}

export function extractEmailAddress(value) {
  if (!value) return '';
  if (typeof value === 'object') {
    return extractEmailAddress(value.email || value.address || '');
  }
  const s = String(value).trim();
  if (!s) return '';
  const angle = s.match(/<([^>]+)>/);
  const raw = (angle ? angle[1] : s).trim();
  const at = raw.lastIndexOf('@');
  if (at <= 0 || at === raw.length - 1) return '';
  return raw.toLowerCase();
}

export function extractSenderEmail(message) {
  if (!message) return '';
  if (typeof message === 'string') return extractEmailAddress(message);
  return (
    extractEmailAddress(message.fromAddress) ||
    extractEmailAddress(message.fromEmail) ||
    extractEmailAddress(message.from) ||
    extractEmailAddress(message.senderEmail) ||
    ''
  );
}

export function getSenderDomain(emailOrMessage) {
  const addr = typeof emailOrMessage === 'string'
    ? extractEmailAddress(emailOrMessage)
    : extractSenderEmail(emailOrMessage);
  if (!addr) return null;
  const at = addr.lastIndexOf('@');
  if (at < 0) return null;
  let domain = addr.slice(at + 1).replace(/\.$/, '').toLowerCase();
  if (domain.startsWith('www.')) domain = domain.slice(4);
  const parts = domain.split('.');
  if (parts.length >= 3 && MAIL_HOST_PREFIXES.has(parts[0])) {
    domain = parts.slice(1).join('.');
  }
  if (!domain || !domain.includes('.') || CONSUMER_MAILBOX_DOMAINS.has(domain)) {
    return null;
  }
  return domain;
}

/** Google's raw icon: often a small mark centred on a much larger canvas. */
export function getRawSenderLogoUrl(emailOrMessage) {
  const domain = getSenderDomain(emailOrMessage);
  if (!domain) return null;
  return `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=256`;
}

/**
 * Same icon, passed through the wsrv.nl image proxy, which trims the uniform
 * border (white or transparent) and re-crops the result to a full square.
 * Without this a logo like Bugfender's covers only ~57% of its canvas and
 * looks like a sticker floating inside the avatar circle.
 */
export function getSenderLogoUrl(emailOrMessage) {
  const raw = getRawSenderLogoUrl(emailOrMessage);
  if (!raw) return null;
  const source = raw.replace(/^https?:\/\//, '');
  return `https://wsrv.nl/?url=${encodeURIComponent(source)}&trim=10&w=128&h=128&fit=cover&output=png&maxage=30d`;
}

export function getCachedLogoStatus(domain) {
  return logoStatusByDomain.get(domain) || null;
}

export function setCachedLogoStatus(domain, ok) {
  if (!domain) return;
  logoStatusByDomain.set(domain, ok ? 'ok' : 'fail');
}
